import React, { useState, useEffect } from 'react';
// Make sure to import your CSS file, e.g., './BackToTopButton.css'
// or ensure this CSS is part of your global stylesheet.

/**
 * BackToTopButton Component
 *
 * This component provides a floating "Back to Top" button that appears when the user
 * scrolls down the page and smoothly scrolls them back to the top when clicked.
 *
 * Industry Standard Practices:
 * - **State Management:** Uses `useState` to control the button's visibility based on scroll position.
 * - **Side Effects (Event Listeners):** `useEffect` is used to add and remove a scroll event listener,
 * preventing memory leaks by cleaning up the listener when the component unmounts.
 * - **Smooth Scrolling:** Utilizes `window.scrollTo` with `behavior: 'smooth'` for a gentle user experience.
 * - **Conditional Styling with CSS Classes:** The button's visibility is controlled by conditionally
 * adding or removing a CSS class (`visible`) based on the `isVisible` state.
 * - **Accessibility:** Includes `aria-label` for screen readers, making the button's purpose clear
 * to users with assistive technologies.
 * - **Bootstrap & Custom CSS:** Uses a combination of a base Bootstrap button class (`btn`)
 * and custom CSS defined in a separate stylesheet for specific positioning and styling.
 */
const BackToTopButton = () => {
  // State to control the visibility of the button
  const [isVisible, setIsVisible] = useState(false);

  // Function to toggle button visibility based on scroll position
  const toggleVisibility = () => {
    // Show button if user has scrolled down more than 300 pixels
    if (window.scrollY > 300) { // Using window.scrollY for modern browser compatibility
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  };

  // Function to smoothly scroll the window to the top
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,        // Scroll to the very top of the page
      behavior: 'smooth' // Enable smooth scrolling animation
    });
  };

  // Effect hook to add and remove the scroll event listener
  useEffect(() => {
    // Add the event listener when the component mounts
    window.addEventListener('scroll', toggleVisibility);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('scroll', toggleVisibility);
    };
  }, []); // Empty dependency array ensures this effect runs only once on mount and cleanup on unmount

  return (
    // The button element with Bootstrap classes and custom classes for styling
    <button
      onClick={scrollToTop} // Attach the scroll-to-top function to click event
      // Bootstrap's btn, btn-primary for styling, plus the custom class
      className={`btn btn-primary shadow back-to-top-button ${isVisible ? 'visible' : ''}`}
      aria-label="Scroll to top" // Essential for accessibility
    >
      {/* Bootstrap Icon for an upward arrow */}
      <i className='bi bi-arrow-up text-xl'></i>
    </button>
  );
};

export default BackToTopButton;